// Display-only compatibility for historical wording; identifiers and signed URLs stay intact.
const guaranteeDisplay = value => {
    if (Array.isArray(value)) return value.map(guaranteeDisplay);
    if (!value || typeof value !== 'object') return value;
    const displayFields = /^(description|explanation|statement|reason|resultReason|eligibilityTip|paymentUnavailableReason|channelDescription|retouchReasonDescription|retouchResolutionDescription|message)$/;
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key,
        typeof item === 'string' && displayFields.test(key) ? item.replace(/\u62bc\u91d1/g, '保证金')
            : item && typeof item === 'object' ? guaranteeDisplay(item) : item]));
};
const afterSales = require("./afterSales.cjs");
const { REFUND, numericStatus } = require("./photographyStatus.cjs");
const STATUS_TEXT = Object.freeze({ 1: "待处理", 2: "历史已裁决", 3: "历史申诉中", 4: "已结案", 5: "已撤回", 6: "已冲正" });
const getStatusCode = (record) => {
    const value = record && record.status;
    return typeof value === "number" && Number.isInteger(value) && STATUS_TEXT[value] ? value : null;
};
const getStatusText = (record) => STATUS_TEXT[getStatusCode(record)] || "进度待确认";
const getRefundStatusText = (status) => ({ [REFUND.PROCESSING]: "退款处理中", [REFUND.SUCCEEDED]: "退款成功", [REFUND.FAILED]: "退款失败，待恢复", [REFUND.UNKNOWN]: "退款结果待确认" }[numericStatus(status, REFUND)] || "退款进度待确认");
const getRefundDispatchText = (status) => ({ PENDING: "待调度", PROCESSING: "调度中", COMPLETED: "指令已送达渠道", MANUAL_REQUIRED: "需人工处理" }[status] || "");

const createDisputeWorkflow = ({ transport, participant = "USER", onRefresh = async () => {}, errorMessage = (error) => error.message || "操作失败，请重试" }) => {
    const initial = () => ({ conversionKey:"", conversionPayload:null, conversionUncertain:false, conversionServiceAmount:"", conversionExtraAmount:"0.00", conversionReason:"", visible: false, stage: "form", serviceOrderNo: "", mode: "OTHER", description: "",
        files: [], record: null, evidence: [], busy: false, error: "", completed: false,
        afterSalesOptions: null, afterSalesTypeCode: null, afterSalesReasonCode: null, requestedServiceRefundAmount: "", requestedExtraRefundAmount: "", createPayload: null,
        statement: "", appealReason: "", createKey: "", statementKey: "", completionKey: "", appealKey: "", creationUncertain: false,
        statementUncertain: false, completionUncertain: false, appealUncertain: false, recordUnavailable: false });
    const state = initial();
    let alive = true;
    let generation = 0;
    const ensureAlive = () => { if (!alive) throw new Error("页面已离开"); };
    const captureCurrentRequest = () => {
        const requestGeneration = generation;
        return () => alive && generation === requestGeneration;
    };
    const sessions = new Map();
    const draftKey = (serviceOrderNo, mode) => JSON.stringify(["draft", serviceOrderNo, mode]);
    const recordKey = (serviceOrderNo, disputeNo) => JSON.stringify(["record", serviceOrderNo, disputeNo]);
    const sessionKey = () => state.record ? recordKey(state.serviceOrderNo, state.record.disputeNo) : draftKey(state.serviceOrderNo, state.mode);
    const closed = (record) => [4, 5, 6].includes(getStatusCode(record));
    const save = () => {
        if (!state.serviceOrderNo || (state.recordUnavailable && !state.record)) return;
        sessions.set(sessionKey(), { ...state });
    };
    const clearPrivateState = () => {
        sessions.delete(sessionKey());
        Object.assign(state, { conversionKey:"",conversionPayload:null,conversionUncertain:false,conversionServiceAmount:"",conversionExtraAmount:"0.00",conversionReason:"", description: "", files: [], record: null, evidence: [], completed: false,
            afterSalesOptions: null, afterSalesTypeCode: null, afterSalesReasonCode: null, requestedServiceRefundAmount: "", requestedExtraRefundAmount: "", createPayload: null,
            statement: "", appealReason: "", createKey: "", statementKey: "", completionKey: "", appealKey: "", creationUncertain: false,
            statementUncertain: false, completionUncertain: false, appealUncertain: false });
    };
    const errorStatus = (error) => Number(error.code || (error.response && error.response.status));
    const uncertain = (error) => {
        const status = errorStatus(error);
        return !status || status >= 500 || status === 408;
    };
    const newKey = () => `dispute-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const validateRecord = (record, disputeNo) => {
        if (!record || typeof record.disputeNo !== "string" || !record.disputeNo
            || record.serviceOrderNo !== state.serviceOrderNo || (disputeNo && record.disputeNo !== disputeNo)) {
            throw new Error("处理记录与本服务不匹配，请刷新确认");
        }
        return guaranteeDisplay(record);
    };
    const acceptRecord = (record, disputeNo) => {
        const next = validateRecord(record, disputeNo);
        const changed = state.record && (state.record.evidenceRound || 1) !== (next.evidenceRound || 1);
        if (changed) Object.assign(state, {completed:false,completionKey:'',completionUncertain:false,statementKey:'',statementUncertain:false,statement:'',files:[]});
        state.record = next;
        state.completed = state.completed || Boolean(record.myEvidenceCompletedAt);
        return state.record;
    };
    const markBusyResultUncertain = () => {
        if (!state.busy) return;
        state.files.forEach((item) => { if (item.status === "uploading") item.status = "uncertain"; });
        if (!state.record && state.createKey) state.creationUncertain = true;
        if (state.record && state.statementKey && state.statement) state.statementUncertain = true;
        if (state.record && state.completionKey && !state.completed) state.completionUncertain = true;
        if (state.record && state.appealKey && state.appealReason) state.appealUncertain = true;
    };
    const refresh = async (isCurrent = () => alive) => {
        if (!isCurrent()) return;
        try { await onRefresh(); }
        catch (error) { if (isCurrent()) state.error += `；${errorMessage(error, "最新服务状态加载失败，请刷新重试")}`; }
    };
    const reportFailure = async (error, isCurrent = () => alive, readResult = false) => {
        if (!isCurrent()) return;
        state.error = errorMessage(error);
        const status = errorStatus(error);
        if (state.record && (readResult || [401, 403, 404, 409].includes(status))) {
            try {
                const record = await transport.read(state.record.disputeNo);
                if (!isCurrent()) return;
                acceptRecord(record, state.record.disputeNo);
            } catch (readError) {
                if (!isCurrent()) return;
                state.recordUnavailable = true;
                const readStatus = errorStatus(readError);
                if ([401, 403, 404].includes(readStatus)) clearPrivateState();
                state.error += `；${errorMessage(readError, "处理记录刷新失败")}`;
            }
        }
    };
    const uploadPending = async (isCurrent) => {
        if (getStatusCode(state.record) !== 1) return;
        const disputeNo = state.record.disputeNo;
        for (const item of state.files.filter((file) => file.status !== "uploaded")) {
            if (!isCurrent()) return;
            item.status = "uploading";
            try {
                const evidence = await transport.upload(disputeNo, item.file, state.record.evidenceRound || 1);
                if (!isCurrent()) return;
                item.status = "uploaded"; state.evidence.push(evidence);
            } catch (error) {
                if (!isCurrent()) return;
                item.status = uncertain(error) ? "uncertain" : "failed";
                if (item.status === "uncertain") {
                    try { const evidence = await transport.listEvidence(disputeNo); if (isCurrent()) state.evidence = evidence.slice(); }
                    catch (listError) { /* The original failure remains visible; the user can reload the record. */ }
                }
                throw error;
            }
        }
        if (isCurrent()) state.stage = "details";
    };
    const flow = {
        state,
        dispose() { alive = false; sessions.clear(); Object.assign(state, initial()); },
        async open({ serviceOrderNo, mode = "OTHER", disputeNo }) {
            if (!alive) return;
            markBusyResultUncertain();
            save();
            generation++;
            const isCurrent = captureCurrentRequest();
            const newInitiation = !disputeNo;
            const cached = disputeNo ? sessions.get(recordKey(serviceOrderNo, disputeNo)) : sessions.get(draftKey(serviceOrderNo, mode))
                || Array.from(sessions.values()).find((s) => s.serviceOrderNo === serviceOrderNo && s.mode === mode && s.record && !closed(s.record));
            Object.assign(state, initial(), cached || {}, { visible: true, serviceOrderNo, busy: false, error: "", recordUnavailable: false });
            if (!cached) { state.mode = mode; state.stage = mode === "CREATOR_NO_SHOW" ? "confirm" : "form"; }
            if (state.creationUncertain) state.stage = "form";
            disputeNo = disputeNo || (state.record && state.record.disputeNo);
            if (!disputeNo && mode === "AFTER_SALES") {
                if (participant !== "USER") { state.error = "仅预约用户可发起售后"; state.recordUnavailable = true; return; }
                state.busy = true;
                try { const options = await transport.options(serviceOrderNo); if (isCurrent()) state.afterSalesOptions = afterSales.validateOptions(options, serviceOrderNo); }
                catch (error) { if (isCurrent()) { state.afterSalesOptions = null; if ([401, 403, 404].includes(errorStatus(error))) { clearPrivateState(); state.recordUnavailable = true; } state.error = errorMessage(error); } }
                finally { if (isCurrent()) state.busy = false; }
            }
            if (disputeNo) {
                state.stage = "details"; state.busy = true; state.error = "";
                try {
                    const record = await transport.read(disputeNo);
                    if (!isCurrent()) return;
                    acceptRecord(record, disputeNo);
                    if (newInitiation && closed(record)) {
                        Object.assign(state, initial(), { visible: true, serviceOrderNo, mode, stage: mode === "CREATOR_NO_SHOW" ? "confirm" : "form" });
                        return;
                    }
                    const evidence = await transport.listEvidence(disputeNo);
                    if (!isCurrent()) return;
                    state.evidence = evidence.slice();
                } catch (error) {
                    if (!isCurrent()) return;
                    state.recordUnavailable = true;
                    const status = errorStatus(error);
                    if ([401, 403, 404].includes(status)) clearPrivateState();
                    state.error = errorMessage(error);
                    await refresh();
                }
                finally { if (isCurrent()) state.busy = false; }
            }
        },
        wait() { state.visible = false; },
        close(force = false) {
            if (state.busy && !force) return;
            if (force) { markBusyResultUncertain(); save(); generation++; state.busy = false; }
            state.visible = false;
        },
        confirmRetry(index) {
            if (!state.busy && state.files[index] && state.files[index].status === "uncertain") state.files[index].status = "pending";
        },
        removeFile(index) {
            const item = state.files[index];
            if (!state.busy && !state.completed && !state.completionUncertain && item && ["pending", "failed"].includes(item.status)) state.files.splice(index, 1);
        },
        linkEvidence(index, evidenceId) {
            const item = state.files[index];
            if (!state.busy && item && item.status === "uncertain" && state.evidence.some((e) => String(e.evidenceId) === String(evidenceId))) item.status = "uploaded";
        },
        async access(evidenceId) {
            if (!alive || !state.record || state.recordUnavailable || !state.evidence.some((item) => String(item.evidenceId) === String(evidenceId))) throw new Error("证据不存在或无权读取");
            const isCurrent = captureCurrentRequest();
            try {
                const result = await transport.access(state.record.disputeNo, evidenceId);
                ensureAlive();
                return result;
            } catch (error) {
                await reportFailure(error, isCurrent);
                if (isCurrent()) await refresh();
                throw error;
            }
        },
        continueSubmission() { state.stage = "form"; },
        setDescription(value) { if (!state.busy && !state.creationUncertain && !state.createPayload && !state.record) state.description = value; },
        setAfterSalesType(value) { if (!state.busy && !state.createPayload && !state.record) state.afterSalesTypeCode = value; },
        setAfterSalesReason(value) { if (!state.busy && !state.createPayload && !state.record) state.afterSalesReasonCode = value; },
        setRefundAmount(value) { if (!state.busy && !state.createPayload && !state.record) state.requestedServiceRefundAmount = value; },
        useFullRefund() { if (state.afterSalesOptions) flow.setRefundAmount(afterSales.money(state.afterSalesOptions.remainingServiceRefundAmount)); },
        setExtraRefundAmount(value) { if (!state.busy && !state.createPayload && !state.record) state.requestedExtraRefundAmount = value; },
        useFullExtraRefund() { if (state.afterSalesOptions) flow.setExtraRefundAmount(afterSales.money(state.afterSalesOptions.remainingExtraRefundAmount)); },
        setStatement(value) { if (!state.busy && !state.completed && !state.statementUncertain && state.record) state.statement = value; },
        setAppealReason(value) { if (!state.busy && !state.appealUncertain && state.record) state.appealReason = value; },
        addFiles(files) {
            if (!alive || state.busy || state.completed || state.completionUncertain || state.recordUnavailable || (state.record && getStatusCode(state.record) !== 1)) return;
            const errors = [];
            Array.from(files).forEach((file) => {
                let error = "";
                if (!["image/jpeg", "image/png", "image/webp", "video/mp4"].includes(file.type)) error = "仅支持 JPG、PNG、WebP 或 MP4";
                else if (!Number.isFinite(file.size) || file.size <= 0) error = "文件为空";
                else if (file.size > 20 * 1024 * 1024) error = "单个文件不能超过 20 MB（20 MiB）";
                if (error) errors.push(`${file.name}：${error}`);
                else state.files.push({ file, status: "pending", confirmedEvidenceId: "" });
            });
            state.error = errors.join("；");
        },
        async submit() {
            if (!alive || state.busy) return;
            if (state.record) return flow.upload();
            if (state.mode === "AFTER_SALES" && participant !== "USER") { state.error = "仅预约用户可发起售后"; return; }
            if (!state.description.trim() || state.description.trim().length > 200) { state.error = "请填写 1–200 字的异常说明"; return; }
            if (!state.files.length && !["AFTER_SALES", "UNSELECTED_REFUND"].includes(state.mode)) { state.error = "请至少选择一份证据"; return; }
            const isCurrent = captureCurrentRequest();
            state.busy = true; state.error = "";
            try {
                if (!state.record) {
                    if (state.mode === "AFTER_SALES") {
                        if (state.createPayload) {
                            const options = afterSales.validateOptions(await transport.options(state.serviceOrderNo), state.serviceOrderNo);
                            if (!isCurrent()) return;
                            state.afterSalesOptions = options;
                            if (options.activeDisputeNo) await transport.read(options.activeDisputeNo);
                            if (!isCurrent()) return;
                        } else state.createPayload = afterSales.payload(state);
                    }
                    if (!state.createKey) state.createKey = newKey();
                    try {
                        const record = await transport.create(state.serviceOrderNo, state.mode, state.description.trim(), state.createKey, state.createPayload);
                        if (!isCurrent()) return;
                        state.record = validateRecord(record);
                        sessions.delete(draftKey(state.serviceOrderNo, state.mode));
                        state.creationUncertain = false;
                    } catch (error) {
                        if (isCurrent()) {
                            state.creationUncertain = uncertain(error);
                            if (!state.creationUncertain) { state.createPayload = null; state.createKey = ''; }
                        }
                        throw error;
                    }
                }
                state.stage = "details";
                if (state.files.length) await uploadPending(isCurrent);
            } catch (error) { await reportFailure(error, isCurrent); }
            finally { if (isCurrent()) { await refresh(isCurrent); if (isCurrent()) state.busy = false; } }
        },
        async upload() {
            if (getStatusCode(state.record) !== 1) return;
            if (!alive || state.busy || !state.record || state.completed || state.completionUncertain || state.recordUnavailable) return;
            if (state.files.some((item) => item.status === "uncertain")) {
                state.error = "上传结果待确认。请先查看已存证据，确认该文件未上传后再重试，避免重复追加。";
                return;
            }
            const isCurrent = captureCurrentRequest();
            state.busy = true; state.error = "";
            try { await uploadPending(isCurrent); }
            catch (error) { await reportFailure(error, isCurrent); }
            finally { if (isCurrent()) { await refresh(isCurrent); if (isCurrent()) state.busy = false; } }
        },
        async convert(payload) {
            const record=state.record;
            if (!alive || state.busy || participant !== "USER" || !record || getStatusCode(record)!==1 || record.disputeType!=="AFTER_SALES" || record.afterSalesTypeCode!==2 || record.retouchReasonCode || record.decidedAt || state.recordUnavailable) return;
            const isCurrent=captureCurrentRequest(); state.error="";
            try {
                const input=state.conversionPayload || payload;
                const service=afterSales.cents(input.requestedServiceRefundAmount),extra=afterSales.cents(input.requestedExtraRefundAmount);
                if(service==null || extra==null || service+extra<=0 || service>afterSales.cents(record.remainingServiceRefundAmount) || extra>afterSales.cents(record.remainingExtraRefundAmount) || !input.reason || !input.reason.trim() || input.reason.trim().length>200) throw new Error("请填写有效分项退款金额及1至200字理由");
                state.conversionPayload={requestedServiceRefundAmount:input.requestedServiceRefundAmount,requestedExtraRefundAmount:input.requestedExtraRefundAmount,reason:input.reason.trim()};
                state.conversionKey=state.conversionKey || newKey(); state.busy=true;
                const updated=await transport.convert(record.disputeNo,state.conversionPayload,state.conversionKey);
                if(!isCurrent())return;
                acceptRecord(updated,record.disputeNo); state.conversionUncertain=false;state.conversionPayload=null;state.conversionKey="";
                const evidence=await transport.listEvidence(record.disputeNo); if(isCurrent())state.evidence=evidence.slice();
            } catch(error) {
                if(isCurrent()) {state.conversionUncertain=uncertain(error); if(!state.conversionUncertain){state.conversionKey="";state.conversionPayload=null;} await reportFailure(error,isCurrent);}
            } finally {if(isCurrent()){state.busy=false;await refresh(isCurrent);}}
        },
        async complete() {
            if (getStatusCode(state.record) !== 1) return;
            if (!alive || state.busy || !state.record || state.completed || state.recordUnavailable) return;
            if (state.statementUncertain) { state.error = "请先确认补充陈述的提交结果"; return; }
            if (state.files.some((item) => item.status !== "uploaded")) { state.error = "请先完成所有待上传文件"; return; }
            const isCurrent = captureCurrentRequest();
            const disputeNo = state.record.disputeNo;
            state.busy = true; state.error = "";
            try {
                if (!state.completionKey) state.completionKey = newKey();
                await transport.complete(disputeNo, state.completionKey, state.record.evidenceRound || 1);
                if (!isCurrent()) return;
                state.completed = true; state.completionUncertain = false;
                const record = await transport.read(disputeNo);
                if (!isCurrent()) return;
                acceptRecord(record, disputeNo);
            } catch (error) {
                if (isCurrent()) { state.completionUncertain = !state.completed && uncertain(error); await reportFailure(error, isCurrent); }
            }
            finally { if (isCurrent()) { await refresh(isCurrent); if (isCurrent()) state.busy = false; } }
        },
        async submitStatement() {
            if (getStatusCode(state.record) !== 1) return;
            if (!alive || state.busy || !state.record || state.completed || state.recordUnavailable) return;
            const statement = state.statement.trim();
            if (!statement || statement.length > 1000) { state.error = "请填写 1–1000 字补充陈述"; return; }
            const isCurrent = captureCurrentRequest();
            const disputeNo = state.record.disputeNo;
            state.busy = true; state.error = "";
            try {
                if (!state.statementKey) state.statementKey = newKey();
                try { await transport.statement(disputeNo, statement, state.statementKey); }
                catch (error) {
                    if (isCurrent()) { state.statementUncertain = uncertain(error); await reportFailure(error, isCurrent); }
                    return;
                }
                if (!isCurrent()) return;
                state.statement = ""; state.statementKey = ""; state.statementUncertain = false;
                try {
                    const record = await transport.read(disputeNo);
                    if (!isCurrent()) return;
                    acceptRecord(record, disputeNo);
                } catch (error) { if (isCurrent()) await reportFailure(error, isCurrent); }
            } finally { if (isCurrent()) { await refresh(isCurrent); if (isCurrent()) state.busy = false; } }
        },
        async withdraw() {
            if (getStatusCode(state.record) !== 1) return;
            if (!alive || state.busy || !state.record || state.recordUnavailable) return;
            const isCurrent = captureCurrentRequest();
            const disputeNo = state.record.disputeNo;
            state.busy = true; state.error = "";
            try {
                const updated = await transport.withdraw(disputeNo);
                if (!isCurrent()) return;
                acceptRecord(updated, disputeNo);
            } catch (error) { await reportFailure(error, isCurrent, true); }
            finally { if (isCurrent()) { await refresh(isCurrent); if (isCurrent()) state.busy = false; } }
        },
        async appeal() {
            // 兼容已绑定的旧页面回调；任何案件阶段均不再发起新申诉。
            return false;
        },
        async accessArrival() {
            if (!alive || !state.record || state.recordUnavailable || state.record.arrivalId == null) throw new Error("没有可读取的到场照片");
            const isCurrent = captureCurrentRequest();
            try {
                const result = await transport.accessArrival(state.serviceOrderNo, state.record.arrivalId);
                if (!isCurrent()) throw new Error("处理记录已切换，请重新打开");
                return result;
            } catch (error) {
                await reportFailure(error, isCurrent);
                if (isCurrent()) await refresh(isCurrent);
                throw error;
            }
        },
    };
    return flow;
};

module.exports = { createDisputeWorkflow, getStatusCode, getStatusText, getRefundStatusText, getRefundDispatchText, afterSales };
